import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"
import { FontAwesome5 } from "@expo/vector-icons"
import * as DocumentPicker from "expo-document-picker"
import React, { useCallback, useEffect } from "react"
import { Image, TouchableOpacity, View } from "react-native"
import PdfViewer from "@common/components/pdf/PdfViewer"
import { checkIsPdf } from "@common/components/pdf/pdfHelpers"

// Conditional import for web
let useDropzone
if (IS_WEB) {
    useDropzone = require("react-dropzone").useDropzone
}

// Accepted file types
const ACCEPTED_TYPES = {
    "image/*": [],
    "application/pdf": [],
}
const ACCEPTED_TYPES_MOBILE = ["image/*", "application/pdf"]

/**
 * File upload zone with drag-drop area and preview
 * Supports images and PDF files
 *
 * Contextual states:
 *  - When `disabled` is true (e.g. class or subject not yet picked) the zone
 *    shows a lock + the `disabledLabel` copy, blocks drop/paste/click, and
 *    calls `onTryActivateWhenDisabled` so the parent can nudge the tutor to
 *    fill whatever is missing.
 *  - When `contextLabel` is provided ("4ème · Maths"), the zone injects it
 *    inline so the tutor sees the upload is scoped to their current selection.
 *
 * @param {Function} onImageSelect - Receives the dropped, picked or pasted file. Omitted (the preview of a loaded file), the zone listens to no paste.
 * @param {boolean} fullHeight - If true, image takes full available height (for side-by-side layout)
 * @param {boolean} noBorder - If true, removes border radius (for panel layout)
 * @param {boolean} disabled - Greyed-out state, drops/clicks/paste become no-ops
 * @param {string|null} contextLabel - Inline scope (e.g. "4ème · Maths") to surface in the copy
 * @param {string|null} disabledLabel - Headline of the locked state, i.e. what the tutor must fill first. Omitted, it falls back to the class+matière wording in the reader's voice.
 * @param {boolean} isParentMode - The zone is also mounted in the parent space, where the reader is vouvoyé
 * @param {Function} onTryActivateWhenDisabled - Called when the tutor clicks the disabled zone
 */
const ImageUploadZone = ({
    uploadedImage,
    onImageSelect,
    onClear,
    fullHeight = false,
    noBorder = false,
    separatorPosition = "after",
    disabled = false,
    contextLabel = null,
    disabledLabel = null,
    isParentMode = false,
    onTryActivateWhenDisabled = null,
}) => {

    const lockedLabel =
        disabledLabel ??
        (isParentMode
            ? "Choisissez d'abord classe et matière"
            : "Choisis d'abord classe et matière")

    const isPDF = uploadedImage?.mimeType === "application/pdf"

    // Separator component
    const Separator = () => (
        <View style={{ flexDirection: "row", alignItems: "center", marginVertical: 20 }}>
            <View style={{ flex: 1, height: 1, backgroundColor: theme.colors.lightMore }} />
            <TextM
                fontSize={14}
                fontWeight="Medium"
                color={theme.colors.grey}
                style={{ marginHorizontal: 15 }}
            >
                OU
            </TextM>
            <View style={{ flex: 1, height: 1, backgroundColor: theme.colors.lightMore }} />
        </View>
    )

    // Handle file drop (web only). No-op when disabled — the visual already
    // tells the tutor what's missing.
    const onDrop = useCallback((acceptedFiles) => {
        if (disabled) return
        if (acceptedFiles?.[0]) {
            const file = acceptedFiles[0]
            onImageSelect({
                uri: URL.createObjectURL(file),
                name: file.name,
                mimeType: file.type,
                size: file.size,
                file: file, // Keep original file for upload
            })
        }
    }, [onImageSelect, disabled])

    // Setup dropzone for web. Pass `disabled` so react-dropzone blocks drop +
    // click + keyboard activation natively.
    const dropzoneProps = IS_WEB ? useDropzone({
        onDrop,
        accept: ACCEPTED_TYPES,
        multiple: false,
        maxFiles: 1,
        noClick: false,
        noKeyboard: false,
        disabled,
    }) : {}

    const { getRootProps, getInputProps, isDragActive } = dropzoneProps

    // Handle Ctrl+V paste (web only) - images only. Skipped while disabled so a
    // paste doesn't silently land into nothing, and when there is no
    // onImageSelect: the preview of a loaded file mounts the zone without one,
    // and the listener is on the whole document.
    useEffect(() => {
        if (!IS_WEB) return
        if (disabled || !onImageSelect) return

        const handlePaste = (event) => {
            const items = event.clipboardData?.items
            if (!items) return

            for (const item of items) {
                if (item.type.startsWith("image/")) {
                    event.preventDefault()
                    const file = item.getAsFile()
                    if (file) {
                        onImageSelect({
                            uri: URL.createObjectURL(file),
                            name: file.name || `pasted-image.${file.type.split("/")[1]}`,
                            mimeType: file.type,
                            size: file.size,
                            file: file,
                        })
                    }
                    break
                }
            }
        }

        document.addEventListener("paste", handlePaste)
        return () => document.removeEventListener("paste", handlePaste)
    }, [onImageSelect, disabled])

    if (uploadedImage) {
        // Preview mode - PDF
        if (isPDF) {
            return (
                <View style={{
                    marginBottom: fullHeight ? 0 : 20,
                    flex: fullHeight ? 1 : undefined,
                    minHeight: fullHeight ? 300 : 200,
                    height: fullHeight ? "100%" : 200,
                    backgroundColor: noBorder ? 'transparent' : theme.colors.lightMore,
                    borderRadius: noBorder ? 0 : 10,
                    overflow: 'hidden',
                }}>
                    <PdfViewer
                        file={uploadedImage.uri}
                        width="100%"
                        height="100%"
                    />
                </View>
            )
        }

        // Preview mode - Image
        return (
            <View style={{
                marginBottom: fullHeight ? 0 : 20,
                flex: fullHeight ? 1 : undefined,
                minHeight: fullHeight ? 300 : undefined,
            }}>
                <Image
                    source={{ uri: uploadedImage.uri }}
                    style={{
                        width: "100%",
                        height: fullHeight ? "100%" : 200,
                        minHeight: fullHeight ? 300 : 200,
                        borderRadius: noBorder ? 0 : 10,
                        resizeMode: "contain",
                        backgroundColor: noBorder ? 'transparent' : theme.colors.lightMore,
                    }}
                />
            </View>
        )
    }

    // Disabled-state copy (scope not resolved yet). Same wording on both web
    // and mobile — the visual cue is what does the heavy lifting. The parent
    // overrides the headline when what's missing isn't class+matière (e.g. in
    // study-program mode the scope comes from the program chapter).
    const renderDisabledContent = () => (
        <>
            <View style={{ marginBottom: 10 }}>
                <FontAwesome5
                    name="lock"
                    size={26}
                    color={theme.colors.grey}
                />
            </View>
            <TextM
                fontSize={15}
                fontWeight="Bold"
                color={theme.colors.grey}
                style={{ textAlign: "center" }}
                wrap
            >
                {lockedLabel}
            </TextM>
            <TextM
                fontSize={12}
                color={theme.colors.grey}
                style={{ marginTop: 4, textAlign: "center" }}
                wrap
            >
                pour analyser un fichier
            </TextM>
        </>
    )

    // Activated-state copy. Falls back to the original wording if the parent
    // doesn't supply a contextLabel — keeps the component reusable outside of
    // the GenerateActivityModal.
    const renderActiveContent = () => (
        <>
            <View style={{ flexDirection: "row", marginBottom: 10, gap: 15 }}>
                <FontAwesome5
                    name="image"
                    size={28}
                    color={isDragActive ? theme.colors.success : theme.colors.primary}
                />
                <FontAwesome5
                    name="file-pdf"
                    size={28}
                    color={isDragActive ? theme.colors.success : theme.colors.primary}
                />
            </View>
            <TextM
                fontSize={16}
                fontWeight="Bold"
                color={isDragActive ? theme.colors.success : theme.colors.primary}
                style={{ textAlign: "center" }}
                wrap
            >
                {isDragActive
                    ? "Déposer le fichier ici"
                    : contextLabel
                        ? `Glisser un exercice de ${contextLabel}`
                        : "Glisser-déposer une image ou un PDF"}
            </TextM>
            {contextLabel && !isDragActive && (
                <TextM
                    fontSize={12}
                    color={theme.colors.primary}
                    style={{ marginTop: 4, textAlign: "center", opacity: 0.85 }}
                    wrap
                >
                    {/* Says exactly what the scan does: the chapter is detected,
                        a subject mismatch is only suggested (the tutor confirms),
                        and the class is corrected silently. An older "Sam détecte
                        tout" over-promised, and tutors left a wrong subject
                        selected because of it. */}
                    Sam détecte le chapitre et te prévient si la matière ne correspond pas
                </TextM>
            )}
            <TextM
                fontSize={12}
                color={theme.colors.grey}
                style={{ marginTop: 6, textAlign: "center" }}
                wrap
            >
                {IS_WEB
                    ? "ou cliquer · Ctrl+V pour coller"
                    : "Cliquer pour sélectionner un fichier"}
            </TextM>
        </>
    )

    // Web upload mode with react-dropzone
    if (IS_WEB) {
        // When disabled, we wrap the (still-rendered) drop area in a click
        // catcher that fires `onTryActivateWhenDisabled` so the parent can
        // scroll/focus the empty dropdown. We deliberately keep the drop
        // surface visible (no display:none) to preserve the spatial cue.
        const baseStyle = {
            borderWidth: 2,
            borderStyle: "dashed",
            borderColor: disabled
                ? theme.colors.lightMore
                : isDragActive
                    ? theme.colors.success
                    : theme.colors.primary,
            borderRadius: 10,
            paddingTop: 30,
            paddingBottom: 30,
            paddingLeft: 20,
            paddingRight: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: separatorPosition === "after" ? 20 : 0,
            backgroundColor: disabled
                ? theme.colors.lighter
                : isDragActive
                    ? theme.colors.successLight
                    : theme.colors.lightMore,
            opacity: disabled ? 0.65 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
            transition: "opacity 200ms ease, background-color 200ms ease, border-color 200ms ease",
        }

        if (disabled) {
            return (
                <>
                    {separatorPosition === "before" && <Separator />}
                    <div
                        onClick={() => onTryActivateWhenDisabled && onTryActivateWhenDisabled()}
                        style={baseStyle}
                    >
                        {renderDisabledContent()}
                    </div>
                    {separatorPosition === "after" && <Separator />}
                </>
            )
        }

        return (
            <>
                {separatorPosition === "before" && <Separator />}
                <div
                    {...getRootProps()}
                    style={baseStyle}
                >
                    <input {...getInputProps()} />
                    {renderActiveContent()}
                </div>
                {separatorPosition === "after" && <Separator />}
            </>
        )
    }

    // Mobile upload mode with DocumentPicker
    return (
        <>
            {separatorPosition === "before" && <Separator />}
            <TouchableOpacity
                onPress={async () => {
                    if (disabled) {
                        if (onTryActivateWhenDisabled) onTryActivateWhenDisabled()
                        return
                    }
                    const result = await DocumentPicker.getDocumentAsync({
                        type: ACCEPTED_TYPES_MOBILE,
                    })
                    if (!result.canceled && result.assets?.[0]) {
                        onImageSelect(result.assets[0])
                    }
                }}
                activeOpacity={disabled ? 1 : 0.6}
                style={{
                    borderWidth: 2,
                    borderStyle: "dashed",
                    borderColor: disabled ? theme.colors.lightMore : theme.colors.primary,
                    borderRadius: 10,
                    paddingVertical: 30,
                    paddingHorizontal: 20,
                    alignItems: "center",
                    marginBottom: separatorPosition === "after" ? 20 : 0,
                    backgroundColor: disabled ? theme.colors.lighter : theme.colors.lightMore,
                    opacity: disabled ? 0.65 : 1,
                }}
            >
                {disabled ? renderDisabledContent() : renderActiveContent()}
            </TouchableOpacity>
            {separatorPosition === "after" && <Separator />}
        </>
    )
}

export default ImageUploadZone
